/* ---------------------------------------------------------------------------
   The bundled register for each department — the fallback, never the truth.

   Every deployment fetches its register from D1 on boot and swaps it in. These
   are what a device shows before that lands and after it fails: a bridge
   inspector on a hillside with one bar of signal gets a complete, honest
   register rather than an empty table. `registerSource()` in
   src/data/register.ts is how a screen says which one is on the screen.

   WHY BOTH SEEDS SHIP IN EVERY BUILD. One build serves every tenant, and the
   seed is the thing that has to work with no network at all — which rules out
   fetching it and rules out a dynamic import, because the moment it is needed is
   the moment nothing can be loaded. Five DoR records and 108 DPWH rows are a few
   kilobytes after compression against a bundle that carries three.js, so the
   arithmetic is not close. If a third department arrives with fifty thousand
   structures, that changes and this is where the change goes: keep the small
   seeds compiled and give the large one a cached-at-install payload rather than
   making every tenant pay for every other tenant's register.

   A TENANT WITH NO SEED GETS AN EMPTY ARRAY, NOT NEPAL'S. `seedFor` returns []
   for an id nobody has bundled a register for, and the screens then say the
   register is empty and where it came from — which is true. Falling back to
   another department's bridges would put the wrong structures on screen under
   the right letterhead, which is the single worst failure this file could have.
   --------------------------------------------------------------------------- */

import type { Bridge } from '../data/types.ts';
import { BRIDGES } from '../data/seed.ts';
import { BRIDGES_DPWH } from '../data/dpwh.ts';
import { FWO_DEMO_BRIDGES, demoEnabled, withDemoBridges } from '../data/fwo-demo.ts';
import type { TenantId } from './types.ts';

const SEEDS: Readonly<Record<TenantId, readonly Bridge[]>> = {
  'nepal-dor': BRIDGES,
  'ph-dpwh': BRIDGES_DPWH,
};

/**
 * The bundled records for one department, before they are copied out.
 *
 * TEMPORARY BRANCH — the FWO demonstration, and the whole of it in this file.
 * Ten fabricated records, and only in a build that asked for them: `demoEnabled`
 * is false unless VITE_FWO_DEMO is set, so the default build still gets the
 * empty register src/tenant/profiles/pk-fwo.ts describes and `hasSeed('pk-fwo')`
 * is still false. Read the header of src/data/fwo-demo.ts before quoting one
 * figure off it, and delete this function with that file — `seedFor` and
 * `hasSeed` go back to reading SEEDS directly.
 *
 * ASKED AT CALL TIME rather than folded into SEEDS above, because a flag
 * resolved once when this module was first imported is a flag no test can turn
 * on: the register would be frozen before any check ran. It is a build constant
 * in a real deployment either way.
 */
function bundled(tenant: TenantId): readonly Bridge[] {
  if (cloudOnly()) return [];
  if (tenant === 'pk-fwo') return demoEnabled() ? FWO_DEMO_BRIDGES : [];
  /* The DPWH demonstration fills in two of the listing's own records; see src/data/dpwh-demo.ts. */
  if (tenant === 'ph-dpwh') return withDemoBridges(tenant, SEEDS[tenant] ?? []);
  return SEEDS[tenant] ?? [];
}

/**
 * True in the cloud build (run-cloud.bat sets VITE_CLOUD=1): no bundled
 * register at all, so every record on screen is one the deployment served. A
 * device that cannot reach it shows an empty register, which is the truth.
 */
function cloudOnly(): boolean {
  try {
    return (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_CLOUD === '1';
  } catch {
    return false;
  }
}

/**
 * The bundled register for one department, or an empty one.
 *
 * Copied on the way out. The seed arrays are module constants and the register
 * is mutated in place by an edit that lands while the backend is unreachable —
 * handing out the constant itself would let a single edit alter the fallback for
 * the rest of the session, so a reload would show the edit as though it had been
 * saved.
 */
export function seedFor(tenant: TenantId): Bridge[] {
  return [...bundled(tenant)];
}

/** True where a department has a bundled register at all. */
export function hasSeed(tenant: TenantId): boolean {
  return bundled(tenant).length > 0;
}
